import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { useOnline } from "@/hooks/useOnline";
import api from "@/lib/api";
import { Package, History, User, Bell, Wifi, WifiOff, LogOut } from "lucide-react";

export default function DriverLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const online = useOnline();
  const [showNotif, setShowNotif] = useState(false);

  const { data: notifs = [], refetch } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 20000,
  });
  const unread = notifs.filter((n) => !n.read).length;

  const markAll = async () => { await api.post("/notifications/read-all"); refetch(); };
  const doLogout = async () => { await logout(); navigate("/login"); };

  const tabs = [
    { to: "/driver", label: "Jobs", icon: Package, end: true },
    { to: "/driver/history", label: "History", icon: History },
    { to: "/driver/profile", label: "Profile", icon: User },
  ];

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col bg-[#F8FAFC]">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[#0B192C] px-4 pb-2 pt-4 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-400">GS Pacific ePOD</div>
            <div className="font-head text-lg font-bold">{user?.name}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { setShowNotif(!showNotif); if (!showNotif && unread) markAll(); }} data-testid="driver-notifications-btn" className="relative rounded-full bg-white/10 p-2">
              <Bell size={20} />
              {unread > 0 && (
                <span data-testid="notif-badge" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#FF6500] px-1 text-[10px] font-bold">
                  {unread}
                </span>
              )}
            </button>
            <button onClick={doLogout} data-testid="driver-logout-btn" className="rounded-full bg-white/10 p-2"><LogOut size={20} /></button>
          </div>
        </div>
        {/* Online status */}
        <div
          data-testid="online-status-banner"
          className={`mt-2 flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold ${
            online ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"
          }`}
        >
          {online ? <Wifi size={14} /> : <WifiOff size={14} />}
          {online ? "ONLINE · GPS Ready" : "OFFLINE — DATA WILL SYNC AUTOMATICALLY"}
        </div>

        {showNotif && (
          <div className="mt-2 max-h-72 overflow-y-auto rounded-xl bg-white p-2 text-slate-900 shadow-xl" data-testid="notif-dropdown">
            {notifs.length === 0 && <div className="px-3 py-6 text-center text-sm text-slate-400">No notifications</div>}
            {notifs.map((n) => (
              <button
                key={n.id}
                onClick={() => { setShowNotif(false); if (n.order_id) navigate(`/driver/delivery/${n.order_id}`); }}
                className="flex w-full flex-col items-start rounded-lg px-3 py-2 text-left hover:bg-slate-50"
              >
                <span className="text-sm font-bold text-[#0B192C]">{n.title}</span>
                <span className="text-xs text-slate-500">{n.body}</span>
              </button>
            ))}
          </div>
        )}
      </header>

      <main className="flex-1 p-4 pb-28">
        <Outlet />
      </main>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 left-1/2 z-30 flex w-full max-w-md -translate-x-1/2 items-center justify-around border-t border-slate-200 bg-white/95 px-2 pb-6 pt-2 backdrop-blur">
        {tabs.map((t) => (
          <NavLink
            key={t.to} to={t.to} end={t.end}
            data-testid={`driver-tab-${t.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex flex-1 flex-col items-center gap-1 rounded-xl py-1.5 text-xs font-semibold ${
                isActive ? "text-[#FF6500]" : "text-slate-400"
              }`
            }
          >
            <t.icon size={24} /> {t.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
